/**
 * @fileoverview "External administration" section for the contact detail
 * panel (company contacts).
 *
 * Lets the user confirm that a company contact is itself in external
 * administration (typically a related entity of the job's own entity) and
 * record: the status label ("In Liquidation"), the appointee — another job
 * contact, usually the appointed practitioner linked to their insolvency
 * firm — and the appointee's title ("Liquidator"). These feed the
 * `contact_job_external_admin_*` document fields; letters to the appointee
 * bind those parts for their address block. The company's own name/address
 * fields are not redirected.
 *
 * State is split in two:
 * - `status` / `appointeeTitle` are text and live in a parent-owned draft
 *   with an autosave hook (like `AddressesEditor`); this component fires
 *   `onCommit` on blur and immediately on toggle.
 * - The appointee link is saved directly via `setExternalAdminAppointee`, as
 *   in `ContactPeopleSection`.
 */

import { useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Building2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import type { AutosaveStatus as AutosaveStatusValue } from "@/utils/forms/use-autosave";
import { AutosaveStatus } from "@/utils/forms/autosave-status";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Switch } from "@/ui/primitives/switch";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { ExternalAdminSuggestionBanner } from "./ExternalAdminSuggestionBanner";
import type { ExternalAdminSuggestion, JobContactRow } from "./types";

/**
 * Text draft shape. A non-empty `status` means the company is in external
 * administration; clearing it (or turning the toggle off) removes the overlay.
 */
export interface ExternalAdminDraft {
  status: string;
  appointeeTitle: string;
}

/** Status pre-filled when the toggle is switched on with no status yet. */
const DEFAULT_EXTERNAL_ADMIN_STATUS = "In Liquidation";

interface ExternalAdministrationSectionProps {
  contact: JobContactRow;
  /** Other rows on this job, for the appointee picker. */
  jobContacts: Array<JobContactRow>;
  draft: ExternalAdminDraft;
  onChange: (next: ExternalAdminDraft) => void;
  /** Called after a text value commits (blur / toggle). */
  onCommit?: () => void;
  /**
   * Suggest-only EXAD hint from ingestion (see `ExternalAdminSuggestionBanner`).
   * Undefined once handled or when the source data carries no signal.
   */
  suggestion?: ExternalAdminSuggestion;
  /**
   * Fired after the suggestion is confirmed with the values now on the
   * server so the parent can re-baseline its autosave draft.
   */
  onSuggestionApplied?: (next: ExternalAdminDraft) => void;
  disabled?: boolean;
  /** Autosave indicator state, threaded from the parent's hook. */
  autosaveStatus: AutosaveStatusValue;
  lastSavedAt: number | null;
  onRetry?: () => void;
}

export function ExternalAdministrationSection({
  contact,
  jobContacts,
  draft,
  onChange,
  onCommit,
  suggestion,
  onSuggestionApplied,
  disabled,
  autosaveStatus,
  lastSavedAt,
  onRetry,
}: ExternalAdministrationSectionProps) {
  const setAppointee = useConvexMutation(
    api.contacts.mutations.setExternalAdminAppointee,
  );
  const [selectedContactId, setSelectedContactId] = useState<string>("");

  const isInExternalAdmin = draft.status.trim().length > 0;
  const appointee = contact.externalAdminAppointee;

  const handleToggle = (checked: boolean) => {
    onChange(
      checked
        ? { ...draft, status: DEFAULT_EXTERNAL_ADMIN_STATUS }
        : { status: "", appointeeTitle: "" },
    );
    onCommit?.();
  };

  const runSetAppointee = async (
    appointeeContactId: Id<"jobContacts"> | null,
    success: string,
  ) => {
    try {
      await setAppointee({ companyContactId: contact._id, appointeeContactId });
      toast.success(success);
      setSelectedContactId("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Something went wrong"));
    }
  };

  const appointeeOptions = jobContacts
    .filter((c) => c._id !== contact._id && c.status === "active")
    .map((c) => ({
      value: c._id,
      label: c.displayName,
      title:
        c.contactType === "person" && c.linkedCompanyName
          ? `${c.displayName} — ${c.linkedCompanyName}`
          : undefined,
    }));

  return (
    <section
      className={cn(
        "flex flex-col gap-3",
        // Once the company is marked as in external administration, group the
        // toggle and the details being recorded so they read as one block.
        isInExternalAdmin && "rounded-lg border border-border bg-muted/20 p-3",
      )}
      aria-label="External administration"
    >
      {suggestion && !isInExternalAdmin && (
        <ExternalAdminSuggestionBanner
          companyContactId={contact._id}
          suggestion={suggestion}
          disabled={disabled}
          onApplied={onSuggestionApplied}
        />
      )}

      {/* Compact toggle row — no section heading (the label carries the
          meaning). The autosave indicator sits inline; it renders nothing
          while idle, so the row is just text + switch at rest. */}
      <div className="flex items-center justify-between gap-3">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-xs text-muted-foreground underline decoration-dotted underline-offset-2">
                Mark company as in external administration
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px]">
              Turn this on to record the status, the appointed practitioner, and
              their title. Those details fill the external administration fields
              in documents.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex items-center gap-2">
          <AutosaveStatus
            status={autosaveStatus}
            lastSavedAt={lastSavedAt}
            onRetry={onRetry}
          />
          <Switch
            checked={isInExternalAdmin}
            onCheckedChange={handleToggle}
            disabled={disabled}
            aria-label="Mark company as in external administration"
          />
        </div>
      </div>

      {isInExternalAdmin && (
        <>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              Status
            </label>
            <Input
              value={draft.status}
              placeholder={`e.g. ${DEFAULT_EXTERNAL_ADMIN_STATUS}`}
              disabled={disabled}
              onChange={(e) => onChange({ ...draft, status: e.target.value })}
              onBlur={() => onCommit?.()}
            />
            <p className="text-[11px] text-muted-foreground/80">
              This can be added to the company name on document generation.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              Appointee
            </span>
            {appointee ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex min-w-0 items-center gap-2">
                    {appointee.contactType === "person" ? (
                      <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate text-sm font-medium text-foreground">
                      {appointee.displayName}
                    </span>
                  </div>
                  {appointee.firm && (
                    <span className="truncate pl-6 text-xs text-muted-foreground">
                      {appointee.firm.displayName}
                    </span>
                  )}
                  {appointee.contactType === "person" && !appointee.firm && (
                    <span className="pl-6 text-[11px] text-amber-700 dark:text-amber-400">
                      Not linked to a firm — link them as a contact person of
                      their firm so the firm name and address fill in.
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 text-xs"
                  disabled={disabled}
                  onClick={() => runSetAppointee(null, "Cleared the appointee")}
                >
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border bg-muted/10 p-3">
                <p className="text-[11px] text-muted-foreground/80">
                  Select appointed practitioner (or their firm) from job
                  contacts. Add contact first if they are missing from
                  selection.
                </p>
                {appointeeOptions.length > 0 ? (
                  <div className="flex gap-2">
                    <SearchableSelect
                      value={selectedContactId}
                      onValueChange={setSelectedContactId}
                      options={appointeeOptions}
                      placeholder="Choose the appointee…"
                      searchPlaceholder="Search contacts…"
                    />
                    <Button
                      size="sm"
                      className="h-8 shrink-0 text-xs"
                      disabled={disabled || !selectedContactId}
                      onClick={() =>
                        runSetAppointee(
                          selectedContactId as Id<"jobContacts">,
                          "Recorded the appointee",
                        )
                      }
                    >
                      Set
                    </Button>
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground/80">
                    No other active contacts on this job yet.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              Appointee title
            </label>
            <Input
              value={draft.appointeeTitle}
              placeholder="e.g. Liquidator"
              disabled={disabled}
              onChange={(e) =>
                onChange({ ...draft, appointeeTitle: e.target.value })
              }
              onBlur={() => onCommit?.()}
            />
          </div>
        </>
      )}
    </section>
  );
}
